import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  QueryConstraint,
  Timestamp,
  increment,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config';
import { Contact, getContactsInList } from './contactService';
import { v4 as uuidv4 } from 'uuid';

// Types
export interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  preheader?: string;
  content: string;
  headerEnabled: boolean;
  footerEnabled: boolean;
  header?: string;
  footer?: string;
  fromName: string;
  fromEmail: string;
  replyToEmail?: string;
  listIds: string[];
  status: CampaignStatus;
  scheduleDate?: number;
  sentAt?: number;
  stats: CampaignStats;
  createdAt: number;
  updatedAt: number;
  scheduledAt?: number;
  completedAt?: number;
  templateId?: string;
}

export type CampaignStatus = 
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'canceled'
  | 'active'
  | 'completed'
  | 'paused'
  | 'error';

export interface CampaignStats {
  sent: number;
  delivered: number;
  opens: number;
  uniqueOpens: number;
  clicks: number;
  uniqueClicks: number;
  unsubscribes: number;
  bounces: number;
  complaints: number;
}

export interface CampaignRecipient {
  id: string;
  campaignId: string;
  contactId: string;
  email: string;
  status: 'pending' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'complained' | 'unsubscribed';
  sentAt?: number;
  deliveredAt?: number;
  openedAt?: number;
  clickedAt?: number;
  bounceType?: 'hard' | 'soft';
  bouncedAt?: number;
  complaintAt?: number;
  unsubscribedAt?: number;
  clicks?: Array<{
    url: string;
    timestamp: number;
  }>;
  createdAt: number;
  updatedAt: number;
}

// Collection references
const campaignsRef = collection(db, 'emailCampaigns');
const recipientsRef = (campaignId: string) => collection(db, 'emailCampaigns', campaignId, 'recipients');

// Campaign CRUD operations
export const getCampaigns = async (
  pageSize: number = 20,
  lastVisible?: DocumentSnapshot,
  filters?: { field: string; operator: string; value: any }[]
): Promise<{ campaigns: EmailCampaign[]; lastVisible: DocumentSnapshot | null }> => {
  try {
    const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc'), limit(pageSize)];
    
    // Apply filters if provided
    if (filters && filters.length > 0) {
      filters.forEach(filter => {
        constraints.push(where(filter.field, filter.operator as any, filter.value));
      });
    }
    
    // Apply pagination if lastVisible is provided
    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }
    
    const q = query(campaignsRef, ...constraints);
    const snapshot = await getDocs(q);
    
    const campaigns = snapshot.docs.map(doc => (({
      ...doc.data(),
      id: doc.id
    }) as EmailCampaign));
    const newLastVisible = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    
    return { campaigns, lastVisible: newLastVisible };
  } catch (error) {
    console.error('Error getting campaigns:', error);
    throw error;
  }
};

export const getCampaignById = async (id: string): Promise<EmailCampaign | null> => {
  try {
    const docRef = doc(campaignsRef, id);
    const docSnap = await getDoc(docRef);
    
    return docSnap.exists() ? { ...docSnap.data(), id: docSnap.id } as EmailCampaign : null;
  } catch (error) {
    console.error('Error getting campaign:', error);
    throw error;
  }
};

export const createCampaign = async (
  campaignData: Omit<EmailCampaign, 'id' | 'createdAt' | 'updatedAt' | 'stats'>
): Promise<EmailCampaign> => {
  const campaignId = uuidv4();
  const timestamp = Date.now();
  
  const campaign: EmailCampaign = {
    id: campaignId,
    ...campaignData,
    createdAt: timestamp,
    updatedAt: timestamp,
    status: campaignData.status || 'draft',
    stats: {
      sent: 0,
      delivered: 0,
      opens: 0,
      uniqueOpens: 0,
      clicks: 0,
      uniqueClicks: 0,
      unsubscribes: 0,
      bounces: 0,
      complaints: 0
    }
  };
  
  await setDoc(doc(campaignsRef, campaignId), campaign);
  return campaign;
};

export const updateCampaign = async (id: string, campaignData: Partial<EmailCampaign>): Promise<void> => {
  try {
    // Ensure we don't overwrite certain fields that should be updated differently
    const { stats, createdAt, ...safeData } = campaignData as any;
    
    await updateDoc(doc(campaignsRef, id), {
      ...safeData,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }
};

export const deleteCampaign = async (id: string): Promise<void> => {
  try {
    const campaignRef = doc(campaignsRef, id);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) {
      throw new Error(`Campaign with ID ${id} not found`);
    }
    
    const campaign = campaignSnap.data() as EmailCampaign;
    
    // Only allow deletion of draft or canceled campaigns
    if (campaign.status !== 'draft' && campaign.status !== 'canceled') {
      throw new Error(`Cannot delete campaign with status "${campaign.status}"`);
    }
    
    // Delete associated recipients
    await deleteAllCampaignRecipients(id);
    
    // Delete the campaign
    await deleteDoc(campaignRef);
  } catch (error) {
    console.error('Error deleting campaign:', error);
    throw error;
  }
};

// Campaign status operations
export const scheduleCampaign = async (id: string, scheduleDate: number): Promise<void> => {
  try {
    const campaignRef = doc(campaignsRef, id);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) {
      throw new Error(`Campaign with ID ${id} not found`);
    }
    
    const campaign = campaignSnap.data() as EmailCampaign;
    
    // Ensure campaign is in draft status
    if (campaign.status !== 'draft') {
      throw new Error(`Cannot schedule campaign with status "${campaign.status}"`);
    }
    
    // Schedule the campaign
    await updateDoc(campaignRef, {
      status: 'scheduled',
      scheduleDate,
      updatedAt: Date.now()
    });
    
    // Create recipients if not already created
    await ensureCampaignRecipients(id);
  } catch (error) {
    console.error('Error scheduling campaign:', error);
    throw error;
  }
};

export const sendCampaignNow = async (id: string): Promise<void> => {
  try {
    const campaignRef = doc(campaignsRef, id);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) {
      throw new Error(`Campaign with ID ${id} not found`);
    }
    
    const campaign = campaignSnap.data() as EmailCampaign;
    
    // Ensure campaign is in draft or scheduled status
    if (campaign.status !== 'draft' && campaign.status !== 'scheduled') {
      throw new Error(`Cannot send campaign with status "${campaign.status}"`);
    }
    
    // Update campaign status
    const now = Date.now();
    await updateDoc(campaignRef, {
      status: 'sending',
      scheduleDate: now,
      sentAt: now,
      updatedAt: now
    });
    
    // Create recipients if not already created
    await ensureCampaignRecipients(id);
    
    // Trigger the actual sending through a cloud function or server
    // This is a placeholder - actual sending would be implemented separately
    console.log(`Campaign ${id} will be sent by a cloud function`);
    
    // In a real system, you would call an API or trigger a cloud function to do the actual sending
  } catch (error) {
    console.error('Error sending campaign:', error);
    throw error;
  }
};

export const cancelCampaign = async (id: string): Promise<void> => {
  try {
    const campaignRef = doc(campaignsRef, id);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) {
      throw new Error(`Campaign with ID ${id} not found`);
    }
    
    const campaign = campaignSnap.data() as EmailCampaign;
    
    // Only scheduled campaigns can be canceled
    if (campaign.status !== 'scheduled') {
      throw new Error(`Cannot cancel campaign with status "${campaign.status}"`);
    }
    
    // Cancel the campaign
    await updateDoc(campaignRef, {
      status: 'canceled',
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error canceling campaign:', error);
    throw error;
  }
};

// Campaign recipients operations
export const ensureCampaignRecipients = async (campaignId: string): Promise<number> => {
  try {
    const campaignRef = doc(campaignsRef, campaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) {
      throw new Error(`Campaign with ID ${campaignId} not found`);
    }
    
    const campaign = campaignSnap.data() as EmailCampaign;
    
    // Check if we already have recipients
    const q = query(recipientsRef(campaignId), limit(1));
    const recipientsSnap = await getDocs(q);
    
    if (!recipientsSnap.empty) {
      // Recipients already exist, return count
      const countQ = query(recipientsRef(campaignId));
      const countSnap = await getDocs(countQ);
      return countSnap.size;
    }
    
    // Get all contacts from the campaign's lists
    const contactsMap = new Map<string, Contact>();
    
    for (const listId of campaign.listIds) {
      const contacts = await getContactsInList(listId);
      
      for (const contact of contacts) {
        // Deduplicate contacts across lists and only include contacts with email enabled
        if (!contactsMap.has(contact.id) && contact.preferences.emailEnabled && !contact.unsubscribedAt) {
          contactsMap.set(contact.id, contact);
        }
      }
    }
    
    const contacts = Array.from(contactsMap.values());
    
    // Create recipients in batches
    const now = Date.now();
    let totalRecipients = 0;
    
    // Process in batches to stay within Firestore limits
    for (let i = 0; i < contacts.length; i += 500) {
      const batch = writeBatch(db);
      const batchContacts = contacts.slice(i, i + 500);
      
      for (const contact of batchContacts) {
        const recipientId = crypto.randomUUID();
        
        const recipient: CampaignRecipient = {
          id: recipientId,
          campaignId,
          contactId: contact.id,
          email: contact.email,
          status: 'pending',
          createdAt: now,
          updatedAt: now
        };
        
        batch.set(doc(recipientsRef(campaignId), recipientId), recipient);
        totalRecipients++;
      }
      
      await batch.commit();
    }
    
    // Update campaign stats
    await updateDoc(campaignRef, {
      'stats.sent': totalRecipients,
      updatedAt: now
    });
    
    return totalRecipients;
  } catch (error) {
    console.error('Error creating campaign recipients:', error);
    throw error;
  }
};

export const getCampaignRecipients = async (
  campaignId: string,
  status?: string,
  pageSize: number = 20,
  lastVisible?: DocumentSnapshot
): Promise<{ recipients: CampaignRecipient[]; lastVisible: DocumentSnapshot | null }> => {
  try {
    const constraints: QueryConstraint[] = [
      where('campaignId', '==', campaignId),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    ];
    
    // Apply status filter if provided
    if (status) {
      constraints.push(where('status', '==', status));
    }
    
    // Apply pagination if lastVisible is provided
    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }
    
    const q = query(recipientsRef(campaignId), ...constraints);
    const snapshot = await getDocs(q);
    
    const recipients = snapshot.docs.map(doc => (({
      ...doc.data(),
      id: doc.id
    }) as CampaignRecipient));
    const newLastVisible = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    
    return { recipients, lastVisible: newLastVisible };
  } catch (error) {
    console.error('Error getting campaign recipients:', error);
    throw error;
  }
};

export const updateRecipientStatus = async (
  campaignId: string,
  recipientId: string,
  status: CampaignRecipient['status'],
  additionalData: Record<string, any> = {}
): Promise<void> => {
  try {
    const recipientRef = doc(recipientsRef(campaignId), recipientId);
    const recipientSnap = await getDoc(recipientRef);
    
    if (!recipientSnap.exists()) {
      throw new Error(`Recipient with ID ${recipientId} not found`);
    }
    
    const recipient = recipientSnap.data() as CampaignRecipient;
    const campaignRef = doc(campaignsRef, campaignId);
    const now = Date.now();
    
    // Update the recipient
    await updateDoc(recipientRef, {
      status,
      ...additionalData,
      updatedAt: now
    });
    
    // Update campaign stats based on the status change
    const statsUpdate: Record<string, any> = {
      updatedAt: now
    };
    
    switch (status) {
      case 'delivered':
        statsUpdate['stats.delivered'] = increment(1);
        break;
      case 'opened':
        statsUpdate['stats.opens'] = increment(1);
        if (!recipient.openedAt) {
          statsUpdate['stats.uniqueOpens'] = increment(1);
        }
        break;
      case 'clicked':
        statsUpdate['stats.clicks'] = increment(1);
        if (!recipient.clickedAt) {
          statsUpdate['stats.uniqueClicks'] = increment(1);
        }
        break;
      case 'bounced':
        statsUpdate['stats.bounces'] = increment(1);
        break;
      case 'complained':
        statsUpdate['stats.complaints'] = increment(1);
        break;
    }
    
    // Only update campaign stats if there are changes
    if (Object.keys(statsUpdate).length > 1) {
      await updateDoc(campaignRef, statsUpdate);
    }
  } catch (error) {
    console.error('Error updating recipient status:', error);
    throw error;
  }
};

export const recordUnsubscribe = async (contactId: string, campaignId?: string): Promise<void> => {
  try {
    // If campaignId is provided, update the campaign stats
    if (campaignId) {
      const campaignRef = doc(campaignsRef, campaignId);
      await updateDoc(campaignRef, {
        'stats.unsubscribes': increment(1),
        updatedAt: Date.now()
      });
      
      // Find the recipient record and update it too
      const q = query(
        recipientsRef(campaignId),
        where('contactId', '==', contactId),
        limit(1)
      );
      
      const recipientSnap = await getDocs(q);
      
      if (!recipientSnap.empty) {
        const recipientDoc = recipientSnap.docs[0];
        await updateDoc(doc(recipientsRef(campaignId), recipientDoc.id), {
          unsubscribedAt: Date.now(),
          updatedAt: Date.now()
        });
      }
    }
  } catch (error) {
    console.error('Error recording unsubscribe:', error);
    throw error;
  }
};

export const deleteAllCampaignRecipients = async (campaignId: string): Promise<void> => {
  try {
    // Since Firestore doesn't have a native way to delete all documents in a collection
    // that match a query, we'll fetch them in batches and delete them
    const batchSize = 500;
    let deleted = 0;
    let hasMore = true;
    
    while (hasMore) {
      const q = query(
        recipientsRef(campaignId),
        limit(batchSize)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        hasMore = false;
        break;
      }
      
      const batch = writeBatch(db);
      
      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
        deleted++;
      });
      
      await batch.commit();
      
      // Check if we have more documents to delete
      hasMore = snapshot.size === batchSize;
    }
    
    console.log(`Deleted ${deleted} recipients for campaign ${campaignId}`);
  } catch (error) {
    console.error('Error deleting campaign recipients:', error);
    throw error;
  }
};

// Campaign analytics
export const getAnalyticsOverview = async (campaignId: string): Promise<CampaignStats> => {
  try {
    const campaign = await getCampaignById(campaignId);
    
    if (!campaign) {
      throw new Error(`Campaign with ID ${campaignId} not found`);
    }
    
    return campaign.stats;
  } catch (error) {
    console.error('Error getting campaign analytics:', error);
    throw error;
  }
};

// Update campaign status
export const updateCampaignStatus = async (
  campaignId: string, 
  status: EmailCampaign['status'],
  additionalData: Partial<EmailCampaign> = {}
): Promise<void> => {
  const docRef = doc(campaignsRef, campaignId);
  const statusData: any = { status, updatedAt: Date.now() };
  
  // Add timestamp based on the status
  switch (status) {
    case 'scheduled':
      if (!additionalData.scheduledAt) {
        statusData.scheduledAt = Date.now();
      }
      break;
    case 'active':
    case 'sending':
      if (!additionalData.sentAt) {
        statusData.sentAt = Date.now();
      }
      break;
    case 'completed':
      if (!additionalData.completedAt) {
        statusData.completedAt = Date.now();
      }
      break;
  }
  
  await updateDoc(docRef, { ...statusData, ...additionalData });
};

// Update campaign statistics
export const updateCampaignStats = async (
  campaignId: string, 
  field: keyof EmailCampaign['stats'],
  value: number = 1
): Promise<void> => {
  const docRef = doc(campaignsRef, campaignId);
  await updateDoc(docRef, {
    [`stats.${field}`]: increment(value),
    updatedAt: Date.now()
  });
};

// Add a recipient to a campaign
export const addCampaignRecipient = async (
  campaignId: string, 
  recipientData: Omit<CampaignRecipient, 'id'>
): Promise<CampaignRecipient> => {
  const recipientId = uuidv4();
  const recipient: CampaignRecipient = {
    id: recipientId,
    ...recipientData
  };
  
  await setDoc(doc(recipientsRef(campaignId), recipientId), recipient);
  return recipient;
};

// Add multiple recipients to a campaign
export const addCampaignRecipients = async (
  campaignId: string,
  recipients: Omit<CampaignRecipient, 'id'>[]
): Promise<void> => {
  const batch = writeBatch(db);
  const collectionRef = recipientsRef(campaignId);
  
  for (const recipientData of recipients) {
    const recipientId = uuidv4();
    const recipient: CampaignRecipient = {
      id: recipientId,
      ...recipientData
    };
    
    batch.set(doc(collectionRef, recipientId), recipient);
  }
  
  await batch.commit();
  
  // Update campaign stats
  await updateCampaignStats(campaignId, 'sent', recipients.length);
};

// Get a campaign recipient
export const getCampaignRecipient = async (
  campaignId: string, 
  recipientId: string
): Promise<CampaignRecipient | null> => {
  const docRef = doc(recipientsRef(campaignId), recipientId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return docSnap.data() as CampaignRecipient;
};

// Record a link click
export const recordLinkClick = async (
  campaignId: string,
  recipientId: string,
  url: string
): Promise<void> => {
  const docRef = doc(recipientsRef(campaignId), recipientId);
  const recipientDoc = await getDoc(docRef);
  
  if (!recipientDoc.exists()) {
    return;
  }
  
  const recipient = recipientDoc.data() as CampaignRecipient;
  const clicks = recipient.clicks || [];
  
  clicks.push({
    url,
    timestamp: Date.now()
  });
  
  await updateDoc(docRef, {
    clicks,
    status: 'clicked',
    clickedAt: Date.now()
  });
  
  // Update campaign stats
  await updateCampaignStats(campaignId, 'clicks');
};

// Get campaign performance statistics
export const getCampaignStats = async (campaignId: string): Promise<EmailCampaign['stats'] & {
  openRate: number;
  clickRate: number;
  clickToOpenRate: number;
  bounceRate: number;
  unsubscribeRate: number;
}> => {
  const campaign = await getCampaignById(campaignId);
  
  if (!campaign) {
    throw new Error(`Campaign not found: ${campaignId}`);
  }
  
  const { stats } = campaign;
  const delivered = stats.delivered || stats.sent; // Fallback to sent if delivered is not tracked
  
  // Calculate rates
  const openRate = delivered > 0 ? stats.opens / delivered : 0;
  const clickRate = delivered > 0 ? stats.clicks / delivered : 0;
  const clickToOpenRate = stats.opens > 0 ? stats.clicks / stats.opens : 0;
  const bounceRate = stats.sent > 0 ? stats.bounces / stats.sent : 0;
  const unsubscribeRate = delivered > 0 ? stats.unsubscribes / delivered : 0;
  
  return {
    ...stats,
    openRate,
    clickRate,
    clickToOpenRate,
    bounceRate,
    unsubscribeRate
  };
};

// Get link click data for a campaign
export const getCampaignLinkClickData = async (campaignId: string): Promise<{
  url: string;
  totalClicks: number;
  uniqueClicks: number;
}[]> => {
  // Get all recipients with clicks
  const q = query(
    recipientsRef(campaignId),
    where('status', '==', 'clicked')
  );
  
  const querySnapshot = await getDocs(q);
  const recipients = querySnapshot.docs.map(doc => doc.data() as CampaignRecipient);
  
  // Collect all clicks
  const urlStats: Record<string, { totalClicks: number; uniqueUsers: Set<string> }> = {};
  
  recipients.forEach(recipient => {
    if (!recipient.clicks) return;
    
    const uniqueUrls = new Set<string>();
    
    recipient.clicks.forEach(click => {
      const { url } = click;
      
      if (!urlStats[url]) {
        urlStats[url] = { totalClicks: 0, uniqueUsers: new Set<string>() };
      }
      
      urlStats[url].totalClicks++;
      uniqueUrls.add(url);
    });
    
    // Add recipient to uniqueUsers for each unique URL they clicked
    uniqueUrls.forEach(url => {
      urlStats[url].uniqueUsers.add(recipient.contactId);
    });
  });
  
  // Format the results
  return Object.entries(urlStats).map(([url, stats]) => ({
    url,
    totalClicks: stats.totalClicks,
    uniqueClicks: stats.uniqueUsers.size
  })).sort((a, b) => b.totalClicks - a.totalClicks);
}; 